'use client';

import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Lora } from 'next/font/google';
import type { BlockObjectResponse } from "@notionhq/client/build/src/api-endpoints";
import IFrameWithFullscreen from './IFrameWithFullscreen';
import { ENV_MODE } from "@/constant/common";

const lora = Lora({
  subsets: ['latin'],
  weight: ['500', '700'],
  style: ['normal', 'italic'],
})

// 样式
import 'katex/dist/katex.min.css';
import '../styles/markdown-styles.css';

interface MarkdownRendererProps {
  content: string;
  onHeadingsExtracted?: (headings: Array<{ id: string; text: string; level: number }>) => void;
  blocks?: BlockObjectResponse[];
}

// 生成 slug ID
function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function MarkdownRenderer({ content, onHeadingsExtracted, blocks }: MarkdownRendererProps) {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);



  // 创建 embed blocks 映射表，方便快速查找
  const embedBlocksMap = React.useMemo(() => {
    if (!blocks) return new Map();
    const map = new Map();



    blocks.forEach((block: any) => {


      if ('type' in block && block.type === 'embed') {
        // 尝试多种方式获取 URL
        const embedUrl = block.embed?.url ||
          block[block.type]?.url ||
          (block.embed?.type === 'external' ? block.embed.external?.url : null) ||
          (block.embed?.type === 'file' ? block.embed.file?.url : null);

        if (embedUrl) {
          map.set(block.id, {
            url: embedUrl,
            caption: block.embed?.caption ||
              block[block.type]?.caption ||
              (Array.isArray(block.embed?.caption) ? block.embed.caption.map((c: any) => c.plain_text).join('') : '') ||
              '',
          });
        }
      }
    });

    return map;
  }, [blocks]);


  // 提取标题并通知父组件
  useEffect(() => {
    if (!isMounted || !onHeadingsExtracted) return;

    const extractHeadings = () => {
      const headings: Array<{ id: string; text: string; level: number }> = [];
      // 按照 DOM 中的实际顺序提取所有标题（h1, h2, h3）
      const allHeadings = document.querySelectorAll('.markdown-content h1, .markdown-content h2, .markdown-content h3');

      allHeadings.forEach((element) => {
        const tagName = element.tagName?.toLowerCase();
        const level = parseInt(tagName.substring(1)); // h1 -> 1, h2 -> 2, h3 -> 3
        const text = element.textContent || '';
        const id = element.id || slugify(text);
        if (!element.id) element.id = id;
        headings.push({ id, text, level });
      });

      onHeadingsExtracted(headings);
    };

    // 延迟执行，确保内容已渲染
    const timer = setTimeout(extractHeadings, 100);
    return () => clearTimeout(timer);
  }, [isMounted, content, onHeadingsExtracted]);

  return (
    <div className="markdown-content" style={{ fontFamily: lora.style.fontFamily }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeRaw, rehypeKatex]}
        components={{
          div({ node, ...props }: any) {
            // 检查是否是 embed 占位符
            // 从 props 或 node.properties 中获取 data-embed-block-id
            const blockId = props['data-embed-block-id'] ||
              (node?.properties as any)?.['data-embed-block-id'] ||
              props['dataEmbedBlockId'];

            if (blockId) {
              const embedData = embedBlocksMap.get(blockId);
              if (embedData) {
                // 确保 URL 使用 HTTPS（如果原 URL 是 HTTP，尝试转换为 HTTPS）
                let embedUrl = embedData.url;
                // 内嵌按钮不显示全屏功能
                const allowFullScreen = !embedUrl.startsWith("https://macaron.im/schedule-a-demo") && !embedUrl.startsWith("https://macaron.im/embedded-link-button")
                if (embedUrl.startsWith('http://')) {
                  embedUrl = embedUrl.replace('http://', 'https://');
                } else if (!embedUrl.startsWith('http://') && !embedUrl.startsWith('https://')) {
                  embedUrl = `https://${embedUrl}`;
                }

                // 本地开发时使用 NEXT_PUBLIC_WEB_URL，其他环境使用原始 URL
                const isDevelopment = process.env.NODE_ENV === 'development';
                const isLocalPreviewEnv =
                  process.env.NEXT_PUBLIC_ENV_MODE === ENV_MODE.ALPHA ||
                  process.env.NEXT_PUBLIC_ENV_MODE === ENV_MODE.SIM;
                if (
                  (isDevelopment || isLocalPreviewEnv) &&
                  embedUrl.startsWith("https://macaron.im")
                ) {
                  try {
                    const urlObj = new URL(embedUrl);
                    // 提取路径部分（包括查询参数和哈希）
                    const path = urlObj.pathname + urlObj.search + urlObj.hash;
                    embedUrl = `${process.env.NEXT_PUBLIC_WEB_URL}${path}`;
                  } catch {
                    // URL 解析失败，保持原样
                  }
                }

                // 从 URL 查询参数中提取 h 参数作为 iframe 高度
                let iframeHeight = 600; // 默认高度
                const urlObj = new URL(embedUrl);
                const hParam = urlObj.searchParams.get('h');
                if (hParam) {
                  const parsedHeight = parseInt(hParam);
                  if (!isNaN(parsedHeight) && parsedHeight > 0) {
                    iframeHeight = parsedHeight;
                  }
                }

                return (
                  <IFrameWithFullscreen
                    src={embedUrl}
                    title={embedData.caption || 'Embedded content'}
                    width="100%"
                    height={iframeHeight}
                    style={{
                      margin: '1.5rem 0',
                    }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen={allowFullScreen}
                    loading="lazy"
                  />
                )
              }
            }
            // 普通 div
            return <div {...props} />;
          },
          h1({ node, children, ...props }: any) {
            // 提取文本内容
            const getText = (node: any): string => {
              if (typeof node === 'string') return node;
              if (typeof node === 'number') return String(node);
              if (Array.isArray(node)) return node.map(getText).join('');
              if (node?.props?.children) return getText(node.props.children);
              return '';
            };
            const text = getText(children).trim();
            const id = slugify(text);
            const isReference = /^references?$/i.test(text);
            return (
              <h1 id={id} className={isReference ? 'reference-heading' : ''} {...props}>
                {children}
              </h1>
            );
          },
          h2({ node, children, ...props }: any) {
            // 提取文本内容
            const getText = (node: any): string => {
              if (typeof node === 'string') return node;
              if (typeof node === 'number') return String(node);
              if (Array.isArray(node)) return node.map(getText).join('');
              if (node?.props?.children) return getText(node.props.children);
              return '';
            };
            const text = getText(children).trim();
            const id = slugify(text);
            const isReference = /^references?$/i.test(text);
            return (
              <h2 id={id} className={isReference ? 'reference-heading' : ''} {...props}>
                {children}
              </h2>
            );
          },
          h3({ node, children, ...props }: any) {
            // 提取文本内容
            const getText = (node: any): string => {
              if (typeof node === 'string') return node;
              if (typeof node === 'number') return String(node);
              if (Array.isArray(node)) return node.map(getText).join('');
              if (node?.props?.children) return getText(node.props.children);
              return '';
            };
            const text = getText(children).trim();
            const id = slugify(text);
            const isReference = /^references?$/i.test(text);
            return (
              <h3 id={id} className={isReference ? 'reference-heading' : ''} {...props}>
                {children}
              </h3>
            );
          },
          code({ node, inline, className, children, ...props }: any) {
            const match = /language-(\w+)/.exec(className || '');
            let language = match ? match[1] : '';

            // 代码块（非内联）
            if (!inline && (language || match)) {
              const codeContent = String(children).replace(/\n$/, '');

              // 检测 iframe 标记格式：iframe:URL 或 iframe:URL|title|width|height
              const iframeMatch = codeContent.match(/^iframe:(.+?)(?:\|(.+?))?(?:\|(\d+))?(?:\|(\d+))?$/);

              if (iframeMatch) {
                const url = iframeMatch[1].trim();
                const title = iframeMatch[2]?.trim() || '';
                const width = iframeMatch[3] ? `${iframeMatch[3]}px` : '100%';
                const height = iframeMatch[4] ? `${iframeMatch[4]}px` : '600px';

                // 验证 URL 格式
                try {
                  new URL(url);

                  return (
                    <div className="iframe-wrapper" suppressHydrationWarning>
                      <IFrameWithFullscreen
                        src={url}
                        title={title || 'Embedded content'}
                        width={width}
                        height={height}
                        style={{
                          margin: '1.5rem 0',
                        }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                      />
                      {title && (
                        <p className="iframe-caption">{title}</p>
                      )}
                    </div>
                  );
                } catch {
                  // URL 格式无效，按普通代码块处理
                }
              }

              // 如果是 plain 或 plaintext，默认为 python
              if (language === 'plain' || language === 'plaintext' || !language) {
                language = 'python';
              }

              return (
                <div className="code-block-wrapper" suppressHydrationWarning>
                  {isMounted ? (
                    <SyntaxHighlighter
                      style={prism}
                      language={language}
                      PreTag="div"
                      showLineNumbers={false}
                      wrapLines={false}
                      useInlineStyles={true}
                      customStyle={{
                        margin: 0,
                        padding: 0,
                        background: 'transparent',
                        color: '#2c2c2b',
                        fontSize: '14px',
                        lineHeight: '1.6',
                      }}
                      codeTagProps={{
                        style: {
                          background: 'transparent',
                          color: '#2c2c2b',
                        }
                      }}
                    >
                      {codeContent}
                    </SyntaxHighlighter>
                  ) : (
                    <div style={{ whiteSpace: 'pre', background: 'transparent', color: '#2c2c2b' }}>
                      <code className={className} {...props}>
                        {codeContent}
                      </code>
                    </div>
                  )}
                </div>
              );
            }

            // 内联代码
            return (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          img({ node, src, alt, ...props }: any) {
            if (!src) return null;

            // YouTube 视频
            const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
            const youtubeMatch = src.match(youtubeRegex);

            if (youtubeMatch) {
              const videoId = youtubeMatch[1];
              return (
                <div className="video-wrapper" suppressHydrationWarning>
                  <div className="video-responsive">
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}`}
                      title={alt || 'YouTube video'}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="video-iframe"
                    />
                  </div>
                  {alt && alt !== 'video' && <p className="video-caption">{alt}</p>}
                </div>
              );
            }

            // Vimeo 视频
            const vimeoRegex = /vimeo\.com\/(?:video\/)?(\d+)/;
            const vimeoMatch = src.match(vimeoRegex);

            if (vimeoMatch) {
              const videoId = vimeoMatch[1];
              return (
                <div className="video-wrapper" suppressHydrationWarning>
                  <div className="video-responsive">
                    <iframe
                      src={`https://player.vimeo.com/video/${videoId}`}
                      title={alt || 'Vimeo video'}
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      className="video-iframe"
                    />
                  </div>
                  {alt && alt !== 'video' && <p className="video-caption">{alt}</p>}
                </div>
              );
            }

            // 视频文件
            const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
            const isVideo = videoExtensions.some(ext => src?.toLowerCase().endsWith(ext));

            if (isVideo) {
              return (
                <div className="video-wrapper" suppressHydrationWarning>
                  <video
                    controls
                    preload="metadata"
                    className="video-player"
                    {...props}
                  >
                    <source src={src} type={`video/${src.split('.').pop()}`} />
                    Your browser does not support the video tag.
                  </video>
                  {alt && alt !== 'video' && <p className="video-caption">{alt}</p>}
                </div>
              );
            }

            // 普通图片
            return <img src={src} alt={alt} {...props} />;
          },
          video({ node, src, ...props }: any) {
            return (
              <div className="video-wrapper" suppressHydrationWarning>
                <video
                  controls
                  preload="metadata"
                  className="video-player"
                  {...props}
                >
                  {src && <source src={src} />}
                  Your browser does not support the video tag.
                </video>
              </div>
            );
          },
          table({ node, children, ...props }: any) {
            return (
              <div className="notion-table-wrapper">
                <table {...props}>
                  {children}
                </table>
              </div>
            );
          },
          thead({ node, children, ...props }: any) {
            return <thead {...props}>{children}</thead>;
          },
          tbody({ node, children, ...props }: any) {
            return <tbody {...props}>{children}</tbody>;
          },
          tr({ node, children, ...props }: any) {
            return <tr {...props}>{children}</tr>;
          },
          th({ node, children, ...props }: any) {
            return <th {...props}>{children}</th>;
          },
          td({ node, children, ...props }: any) {
            return <td {...props}>{children}</td>;
          },
          li({ node, children, ...props }: any) {
            // 提取文本内容
            const getText = (node: any): string => {
              if (typeof node === 'string') return node;
              if (typeof node === 'number') return String(node);
              if (Array.isArray(node)) return node.map(getText).join('');
              if (node?.props?.children) return getText(node.props.children);
              return '';
            };

            const text = getText(children);

            // 检查是否是引用项格式：数字. 作者名 (年份) 或包含年份格式
            const isReferenceItem = /^\d+\.\s/.test(text.trim()) && (/\d{4}/.test(text) || /\(/.test(text));

            if (isReferenceItem) {
              // 处理引用格式：识别标题部分（数字后到第一个括号或逗号之前）
              const processReferenceText = (text: string): React.ReactNode => {
                // 匹配格式：数字. 标题 (作者, 年份) 或 [数字] 标题 (作者, 年份)
                // 标题部分是从数字/方括号后到第一个括号或逗号之前的部分
                const titleMatch = text.match(/^(\[?\d+\]?\.?\s*)(.+?)(\s*\(|,\s*[A-Z])/);

                if (titleMatch) {
                  const numberPart = titleMatch[1]; // 数字部分（如 "1. " 或 "[1] "）
                  const titlePart = titleMatch[2].trim(); // 标题部分
                  const restPart = text.substring(titleMatch[0].length); // 剩余部分（作者和年份）

                  return (
                    <>
                      {numberPart}
                      <span style={{ color: 'rgba(0, 0, 0, 0.5)' }}>{titlePart}</span>
                      {restPart}
                    </>
                  );
                }

                // 如果没有匹配到，返回原文本
                return text;
              };

              return (
                <li {...props} className="reference-item">
                  {processReferenceText(text)}
                </li>
              );
            }

            return <li {...props}>{children}</li>;
          },
          p({ node, children, ...props }: any) {
            // 直接检查 children 是否是字符串
            let textContent = '';

            if (typeof children === 'string') {
              textContent = children;
            } else {
              // 提取文本内容
              const extractText = (content: any): string => {
                if (typeof content === 'string') return content;
                if (typeof content === 'number') return String(content);
                if (Array.isArray(content)) {
                  return content.map(extractText).join('');
                }
                if (React.isValidElement(content)) {
                  if (content.type === 'br') {
                    return '\n';
                  }
                  const elementProps = content.props as { children?: any };
                  if (elementProps?.children) {
                    return extractText(elementProps.children);
                  }
                }
                return '';
              };
              textContent = extractText(children);
            }

            // 检测是否包含表格格式（| 开头和结尾的行）
            const lines = textContent.split('\n').map(line => line.trim()).filter(line => line);
            const tableLines = lines.filter(line => line.startsWith('|') && line.endsWith('|'));

            // 如果有至少 2 行表格格式的内容，就渲染为表格
            if (tableLines.length >= 2) {
              // 解析表格内容
              const tableRows: string[][] = [];

              tableLines.forEach((line) => {
                // 移除首尾的 |，然后按 | 分割
                const cells = line.slice(1, -1).split('|').map(cell => cell.trim());

                // 跳过分隔符行（只包含 - 和 : 的行）
                const isSeparator = cells.every(cell => /^:?-+:?$/.test(cell));

                if (!isSeparator && cells.length > 0) {
                  tableRows.push(cells);
                }
              });

              if (tableRows.length >= 1) {
                // 第一行是表头，之后是数据行
                const headerRow = tableRows[0];
                const dataRows = tableRows.slice(1);

                return (
                  <div className="notion-table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          {headerRow.map((cell, index) => (
                            <th key={index}>
                              {cell.split('\n').map((line, lineIndex, array) => (
                                <React.Fragment key={lineIndex}>
                                  {line}
                                  {lineIndex < array.length - 1 && <br />}
                                </React.Fragment>
                              ))}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      {dataRows.length > 0 && (
                        <tbody>
                          {dataRows.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              {row.map((cell, cellIndex) => (
                                <td key={cellIndex}>
                                  {cell.split('\n').map((line, lineIndex, array) => (
                                    <React.Fragment key={lineIndex}>
                                      {line}
                                      {lineIndex < array.length - 1 && <br />}
                                    </React.Fragment>
                                  ))}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      )}
                    </table>
                  </div>
                );
              }
            }

            return <p {...props}>{children}</p>;
          },
          a({ node, href, children, ...props }: any) {
            // 检测是否是引用链接（格式为 [数字] 或 [数字,数字,...]）
            const linkText = String(children).trim();
            const isCitationLink = /^\[\d+(?:,\d+)*\]$/.test(linkText);

            // 如果是引用链接，点击时滚动到 References 部分
            const handleCitationClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();

              // 尝试通过 id 查找 References 标题
              let referencesElement = document.getElementById('references') ||
                document.getElementById('reference') ||
                document.querySelector('[id*="reference" i]');

              // 如果没找到，尝试通过标题查找
              if (!referencesElement) {
                const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
                for (const heading of headings) {
                  const text = heading.textContent?.toLowerCase() || '';
                  if (text.includes('reference')) {
                    referencesElement = heading as HTMLElement;
                    break;
                  }
                }
              }

              // 如果找到了，滚动到该位置
              if (referencesElement) {
                referencesElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              } else {
                // 如果没找到，滚动到页面底部
                window.scrollTo({
                  top: document.documentElement.scrollHeight,
                  behavior: 'smooth'
                });
              }
            };

            // 链接通用样式
            const linkStyle = {
              color: 'rgba(0, 0, 0, 0.5)',
              textDecoration: 'underline',
              textDecorationColor: 'rgba(0, 0, 0, 0.3)',
              textUnderlineOffset: '3px',
              transition: 'color 0.2s ease, text-decoration-color 0.2s ease',
            };

            // 如果是引用链接，使用自定义点击处理
            if (isCitationLink) {
              return (
                <a
                  href="#references"
                  onClick={handleCitationClick}
                  className="cursor-pointer hover:text-black/70 hover:decoration-black/50"
                  style={linkStyle}
                  {...props}
                >
                  {children}
                </a>
              );
            }

            // 普通链接
            return (
              <a
                href={href}
                target={href?.startsWith('http') ? '_blank' : undefined}
                rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="hover:text-black/70 hover:decoration-black/50"
                style={linkStyle}
                {...props}
              >
                {children}
              </a>
            );
          },
          em({ node, children, ...props }: any) {
            // 渲染斜体文本
            return (
              <em {...props} style={{ fontStyle: 'italic' }}>
                {children}
              </em>
            );
          },
          strong({ node, children, ...props }: any) {
            // 渲染粗体文本
            return (
              <strong {...props} style={{ fontWeight: 'bold' }}>
                {children}
              </strong>
            );
          },
          i({ node, children, ...props }: any) {
            // 渲染 <i> 标签（备用的斜体标签）
            return (
              <i {...props} style={{ fontStyle: 'italic' }}>
                {children}
              </i>
            );
          },
          b({ node, children, ...props }: any) {
            // 渲染 <b> 标签（备用的粗体标签）
            return (
              <b {...props} style={{ fontWeight: 'bold' }}>
                {children}
              </b>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
